import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Calendar, Users, Clock } from "lucide-react";
import { useRealAttendance } from "@/hooks/data";
import { useRealStudents } from "@/hooks/data";
import { useState, useEffect } from "react";
import { formatCountWithArabicNumbers } from "@/utils/arabicNumbers";
import { dateSplitter } from "@/utils/formatting/deteSplitter";

export const ClassesAttendanceCard = () => {
  const { students } = useRealStudents();
  const [refreshKey, setRefreshKey] = useState(0);
  // const [localAttendanceRecords, setLocalAttendanceRecords] = useState<any[]>(
  //   []
  // );

  // Get today's date
  const today = dateSplitter(new Date());
  const { useAttendanceByDate } = useRealAttendance();

  const { data: localAttendanceRecords } = useAttendanceByDate(today, 1);

  // Fetch today's attendance data and update local state

  // Load attendance when component mounts or students change
  // useEffect(() => {
  //   const loadTodaysAttendance = async () => {
  //     if (students.length > 0) {
  //       const { data: records } = fetchAttendanceByDate(today);
  //       setLocalAttendanceRecords(records || []);
  //       setRefreshKey((prev) => prev + 1);
  //     }
  //   };
  //   loadTodaysAttendance();
  // }, [fetchAttendanceByDate, students.length, today]);

  // Also update local records when global attendance records change
  // useEffect(() => {
  //   const todaysRecords = attendanceRecords.filter(
  //     (record) => record.date === today
  //   );

  //   if (todaysRecords.length > 0) {
  //     setLocalAttendanceRecords(todaysRecords);
  //     setRefreshKey((prev) => prev + 1);
  //   }
  // }, [attendanceRecords, today]);

  // Get unique grade-section combinations from students
  const getClassesFromStudents = () => {
    const uniqueClasses = new Map<
      string,
      { grade: string; section: string; studentCount: number }
    >();

    students.forEach((student) => {
      const classKey = `${student.grade}_${student.section}`;
      if (uniqueClasses.has(classKey)) {
        const existing = uniqueClasses.get(classKey)!;
        uniqueClasses.set(classKey, {
          ...existing,
          studentCount: existing.studentCount + 1,
        });
      } else {
        uniqueClasses.set(classKey, {
          grade: student.grade,
          section: student.section,
          studentCount: 1,
        });
      }
    });

    return Array.from(uniqueClasses.values());
  };

  // Get classes with and without attendance
  const getClassesAttendanceStatus = () => {
    const allClasses = getClassesFromStudents();
    const classesWithAttendance: Array<{
      grade: string;
      section: string;
      studentCount: number;
    }> = [];
    const classesWithoutAttendance: Array<{
      grade: string;
      section: string;
      studentCount: number;
    }> = [];

    allClasses.forEach((classItem) => {
      // Find students in this class
      const classStudents = students.filter(
        (student) =>
          student.grade === classItem.grade &&
          student.section === classItem.section
      );

      // Check if any student in this class has attendance recorded today using local records
      const hasAttendanceToday = classStudents.some((student) => {
        const hasRecord = localAttendanceRecords.some(
          (record) => record.student_id === student.id && record.date === today
        );

        return hasRecord;
      });

      if (hasAttendanceToday) {
        classesWithAttendance.push(classItem);
      } else {
        classesWithoutAttendance.push(classItem);
      }
    });

    return { classesWithAttendance, classesWithoutAttendance };
  };

  const { classesWithAttendance, classesWithoutAttendance } =
    getClassesAttendanceStatus();

  return (
    <Card
      key={refreshKey}
      className="bg-gradient-to-br from-white/95 to-white/80 backdrop-blur-lg shadow-xl border-0 rounded-2xl lg:rounded-3xl overflow-hidden"
    >
      <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-indigo-600/10"></div>
      <CardHeader className="relative z-10 pb-3 p-3 lg:p-4 bg-secondary text-white rounded-t-2xl lg:rounded-t-3xl">
        <CardTitle className="text-base lg:text-lg font-bold text-white text-left flex items-center justify-start gap-2">
          <Calendar className="w-5 h-5 lg:w-6 lg:h-6 text-white" />
          <span>حالة حضور الفصول</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-3 lg:p-4 pt-4 relative z-10 space-y-3 lg:space-y-4">
        {/* Classes without attendance */}
        {classesWithoutAttendance.length > 0 && (
          <div className="space-y-2 lg:space-y-3">
            <div className="flex items-center gap-2">
              <Clock className="h-3 w-3 lg:h-4 lg:w-4 text-destructive" />
              <span className="font-medium text-destructive text-sm lg:text-base">
                فصول لم يتم تسجيل الحضور لها (
                {formatCountWithArabicNumbers(classesWithoutAttendance.length)})
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {classesWithoutAttendance.map((classItem, index) => (
                <div
                  key={`${classItem.grade}-${classItem.section}-${refreshKey}`}
                  className="flex items-center justify-between p-2 lg:p-3 bg-destructive/5 border border-destructive/20 rounded-lg"
                >
                  <div>
                    <div className="font-medium text-destructive text-sm lg:text-base">
                      {classItem.grade} - {classItem.section}
                    </div>
                    <div className="text-xs lg:text-sm text-destructive/80 flex items-center gap-1">
                      <Users className="h-3 w-3" />
                      {formatCountWithArabicNumbers(
                        classItem.studentCount
                      )}{" "}
                      طالب
                    </div>
                  </div>
                  <div className="w-2 h-2 lg:w-3 lg:h-3 bg-destructive rounded-full"></div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Classes with attendance */}
        {classesWithAttendance.length > 0 && (
          <div className="space-y-2 lg:space-y-3">
            <div className="flex items-center gap-2">
              <Calendar className="h-3 w-3 lg:h-4 lg:w-4 text-primary" />
              <span className="font-medium text-primary text-sm lg:text-base">
                فصول تم تسجيل الحضور لها (
                {formatCountWithArabicNumbers(classesWithAttendance.length)})
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {classesWithAttendance.map((classItem, index) => (
                <div
                  key={`${classItem.grade}-${classItem.section}-${refreshKey}`}
                  className="flex items-center justify-between p-2 lg:p-3 bg-primary/5 border border-primary/20 rounded-lg"
                >
                  <div>
                    <div className="font-medium text-primary text-sm lg:text-base">
                      {classItem.grade} - {classItem.section}
                    </div>
                    <div className="text-xs lg:text-sm text-primary/80 flex items-center gap-1">
                      <Users className="h-3 w-3" />
                      {formatCountWithArabicNumbers(
                        classItem.studentCount
                      )}{" "}
                      طالب
                    </div>
                  </div>
                  <div className="w-2 h-2 lg:w-3 lg:h-3 bg-primary rounded-full"></div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* If no students exist */}
        {students.length === 0 && (
          <div className="text-center py-6 lg:py-8 text-muted-foreground">
            <Calendar className="h-10 w-10 lg:h-12 lg:w-12 mx-auto mb-2 lg:mb-3 text-muted-foreground/40" />
            <p className="text-sm lg:text-base">لا توجد طلاب مسجلون</p>
            <p className="text-xs lg:text-sm mt-1">
              يرجى إضافة طلاب أولاً لعرض حالة الحضور
            </p>
          </div>
        )}

        {/* If students exist but no attendance recorded */}
        {students.length > 0 &&
          classesWithAttendance.length === 0 &&
          classesWithoutAttendance.length > 0 && (
            <div className="text-center py-4 lg:py-6 text-muted-foreground">
              <Clock className="h-8 w-8 lg:h-10 lg:w-10 mx-auto mb-2 text-amber-500" />
              <p className="text-sm lg:text-base font-medium text-amber-700">
                لم يتم تسجيل الحضور بعد
              </p>
              <p className="text-xs lg:text-sm mt-1 text-amber-600">
                يرجى الذهاب لصفحة الحضور لتسجيل حضور الطلاب
              </p>
            </div>
          )}
      </CardContent>
    </Card>
  );
};
